import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import CurrencyInput from '../../components/input/CurrencyInput';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import MontoTooltipLabel from '../../components/labels/MontoTooltipLabel';
import { MonetizationFormData, FormValues } from '../../pages/MonetizationNew';
import { validateDrawerByCuit } from '../../utils/validationUtils';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { useState, useRef, useEffect } from 'react';
import AssetSelectionModal from '../../components/modals/AssetSelectionModal';
import OtrasDivisasButton from '../../components/monetization/OtrasDivisasButton';
import AssetLoadedBanner from '../../components/monetization/AssetLoadedBanner';
import { getQuotations } from '../../services/QuotationsService';
import TextArea from '../../components/textarea/TextArea';

const checkDrawerGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '16px',
  alignItems: 'end' as const,
};

interface CheckSectionProps {
  formData: MonetizationFormData;
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  amountInputStatus?: 'normal' | 'error';
  amountInputMessage?: string;
  currencyCode?: string;
  usdQuotation?: number | null;
}

const CheckSection = ({ formData, formValues, updateFormValues, amountInputStatus = 'normal', amountInputMessage, currencyCode = 'ARS', usdQuotation }: CheckSectionProps) => {
  const fiscalId = useFiscalIdConfig();
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetName, setAssetName] = useState('');
  const hasAsset = !!formValues.asset_id;
  const showLoadButton = !hasAsset;
  const [drawerError, setDrawerError] = useState<string>('');
  const [validatingDrawer, setValidatingDrawer] = useState(false);
  const [lastValidatedCuit, setLastValidatedCuit] = useState<string>('');
  const [drawerManuallyEdited, setDrawerManuallyEdited] = useState(false);
  const [cuitLengthError, setCuitLengthError] = useState<string>('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearingOptions = [
    { id: 0, label: 'NO' },
    { id: 1, label: '24 hs' },
    { id: 2, label: '48 hs' },
  ];

  const clearingItems = clearingOptions.map(option => ({
    label: option.label,
    disabled: false
  }));

  // Cleanup effect para limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Update due_date and estimated_payment_date when relevant fields change
  useEffect(() => {
    let updatedFields: Partial<FormValues> = {};
    
    // Calculate due_date when issue_date or term_in_days changes
    if (formValues.issue_date && formValues.term_in_days !== undefined && formValues.term_in_days !== null) {
      const issueDate = new Date(formValues.issue_date);
      const dueDate = new Date(issueDate);
      dueDate.setDate(dueDate.getDate() + formValues.term_in_days);
      
      const formattedDueDate = dueDate.toISOString().split('T')[0];
      if (formattedDueDate !== formValues.due_date) {
        updatedFields.due_date = formattedDueDate;
        
        // Calculate estimated_payment_date based on new due_date
        const estimatedDate = new Date(dueDate);
        const clearingDays = formValues.clearing === 1 ? 1 : formValues.clearing === 2 ? 2 : 0;
        estimatedDate.setDate(estimatedDate.getDate() + clearingDays);
        
        const formattedEstimatedDate = estimatedDate.toISOString().split('T')[0];
        if (formattedEstimatedDate !== formValues.estimated_payment_date) {
          updatedFields.estimated_payment_date = formattedEstimatedDate;
        }
      }
    }
    
    // Calculate estimated_payment_date when only clearing changes (and due_date exists)
    if (!updatedFields.due_date && formValues.due_date && formValues.clearing !== undefined) {
      const dueDate = new Date(formValues.due_date);
      const estimatedDate = new Date(dueDate);
      const clearingDays = formValues.clearing === 1 ? 1 : formValues.clearing === 2 ? 2 : 0;
      estimatedDate.setDate(estimatedDate.getDate() + clearingDays);
      
      const formattedEstimatedDate = estimatedDate.toISOString().split('T')[0];
      if (formattedEstimatedDate !== formValues.estimated_payment_date) {
        updatedFields.estimated_payment_date = formattedEstimatedDate;
      }
    }
    
    // Update all changed fields in a single call
    if (Object.keys(updatedFields).length > 0) {
      updateFormValues(updatedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.issue_date, formValues.term_in_days, formValues.clearing]);

  const creditorItems = formData.creditors?.map(creditor => ({
    label: creditor.name,
    disabled: false
  })) || [];

  const checkFormatItems = formData.check_format_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const handleCreditorChange = (selectedLabel: string) => {
    const creditor = formData.creditors?.find(c => c.name === selectedLabel);
    if (creditor) {
      updateFormValues({ creditor_id: creditor.id });
    }
  };

  const handleCheckFormatChange = (selectedLabel: string) => {
    const checkFormat = formData.check_format_types?.find(c => c.name === selectedLabel);
    if (checkFormat) {
      updateFormValues({ check_format_type_id: checkFormat.id });
    }
  };

  const handleBankingEntityChange = (selectedLabel: string) => {
    const creditor = formData.creditors?.find(c => c.name === selectedLabel);
    if (creditor) {
      updateFormValues({ 
        banking_entity_id: creditor.id,
        banking_entity_name: creditor.name
      });
    }
  };

  const handleDrawerCuitChange = async (cuit: string) => {
    // Limpiar timeout previo para la validación de longitud
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Actualizar el valor inmediatamente
    updateFormValues({ 
      drawer: { 
        cuit: cuit,
        company_name: formValues.drawer?.company_name || ''
      } 
    });

    // Limpiar error previo inmediatamente
    setCuitLengthError('');

    // Crear timeout para validación de longitud con delay de 1 segundo
    debounceTimeoutRef.current = setTimeout(() => {
      if (cuit && !fiscalId.isValidDigits(cuit)) {
        setCuitLengthError(fiscalId.invalidLengthMessage);
      } else {
        setCuitLengthError('');
      }
    }, 1000);

    await validateDrawerByCuit({
      cuit,
      formValues,
      updateFormValues,
      setDrawerError,
      setValidatingDrawer,
      lastValidatedCuit,
      setLastValidatedCuit,
      drawerManuallyEdited,
      setDrawerManuallyEdited,
      debounceTimeoutRef,
      fiscalIdLabel: fiscalId.label,
      isValidDigits: fiscalId.isValidDigits,
    });
  };

  const handleDrawerCompanyNameChange = (companyName: string) => {
    // Marcar que el drawer fue editado manualmente
    setDrawerManuallyEdited(true);
    
    updateFormValues({ 
      drawer: { 
        cuit: formValues.drawer?.cuit || '',
        company_name: companyName 
      } 
    });
    
    // Ejecutar validación para mostrar el mensaje de edición
    if (formValues.drawer?.cuit && fiscalId.isValidDigits(formValues.drawer.cuit)) {
      validateDrawerByCuit({
        cuit: formValues.drawer.cuit,
        formValues: {
          ...formValues,
          drawer: {
            ...formValues.drawer,
            company_name: companyName
          }
        },
        updateFormValues,
        setDrawerError,
        setValidatingDrawer,
        lastValidatedCuit,
        setLastValidatedCuit,
        drawerManuallyEdited: true,
        setDrawerManuallyEdited,
        debounceTimeoutRef,
        fiscalIdLabel: fiscalId.label,
        isValidDigits: fiscalId.isValidDigits,
      });
    }
  };

  // Helper functions to get selected values for dropdowns
  const getSelectedCreditor = () => {
    const creditor = formData.creditors?.find(c => c.id === formValues.creditor_id);
    return creditor?.name || 'Seleccionar';
  };

  const getSelectedCheckFormat = () => {
    const checkFormat = formData.check_format_types?.find(c => c.id === formValues.check_format_type_id);
    return checkFormat?.name || 'Seleccionar';
  };

  const getSelectedBankingEntity = () => {
    // For edit mode, use the banking_entity_name from the endpoint if available
    if (formValues.banking_entity_name) {
      return formValues.banking_entity_name;
    }
    // Fallback to finding by ID in creditors
    const creditor = formData.creditors?.find(c => c.id === formValues.banking_entity_id);
    return creditor?.name || 'Seleccionar';
  };

  const getSelectedClearing = () => {
    const clearing = clearingOptions.find(c => c.id === formValues.clearing);
    return clearing?.label || 'Seleccionar';
  };

  const handleClearingChange = (selectedLabel: string) => {
    const selected = clearingOptions.find(opt => opt.label === selectedLabel);
    updateFormValues({ clearing: selected?.id });
  };

  const handleObservationChange = (e: { target: { value: string } }) => {
    const value = e.target.value;
    if (value.length <= 2500) {
      updateFormValues({ observation: value });
    }
  };

  useEffect(() => {
    if (!formValues.asset_id || assetName) return;
    getQuotations()
      .then((response) => {
        if (response.success && response.data && Array.isArray(response.data)) {
          const asset = (response.data as Array<{ id: number; name: string }>).find(
            (a) => a.id === formValues.asset_id
          );
          if (asset) setAssetName(asset.name);
        }
      })
      .catch(() => {});
  }, [formValues.asset_id, assetName]);

  const handleAssetAccept = (data: {
    assetId: number;
    assetName: string;
    quantity: number;
    quotation: number;
    calculatedAmount: number;
  }) => {
    setAssetName(data.assetName);
    updateFormValues({
      asset_id: data.assetId,
      asset_quantity: data.quantity,
      asset_quotation: data.quotation,
      monetization_amount: data.calculatedAmount,
    });
    setAssetModalOpen(false);
  };

  const handleAssetClear = () => {
    setAssetName('');
    updateFormValues({
      asset_id: null,
      asset_quantity: null,
      asset_quotation: null,
      monetization_amount: null,
    });
  };

  const checkDataGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '16px',
    alignItems: 'end' as const,
  };

  return (
    <>
      {/* Datos del cheque */}
      <Card margin={{ bottom: '4' }}>
        <div style={{ backgroundColor: '#E6F5F0', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
            Datos del cheque
          </H>
        </div>

        {/* Fila 1: Monto bruto | Divisa + Otras divisas | Tipo CHPD | Formato de cheque */}
        <div
          data-testid="check-data-row-1"
          style={{ ...checkDataGridStyle, marginBottom: hasAsset ? '8px' : '24px' }}
        >
          <Card>
            <MontoTooltipLabel />
            <Card margin={{ top: '1' }}>
              <CurrencyInput
                value={formValues.monetization_amount}
                onValueChange={(val) => updateFormValues({ monetization_amount: val })}
                size="md"
                inputStatus={hasAsset ? 'normal' : amountInputStatus}
                message={hasAsset ? undefined : amountInputMessage}
                readOnly={hasAsset}
              />
            </Card>
          </Card>
          <div style={{ display: 'flex', alignItems: 'end', gap: '8px', minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                label="Divisa"
                value={formData.currency}
                readOnly
                size="md"
                modifier="weak"
              />
            </div>
            {showLoadButton && (
              <OtrasDivisasButton onClick={() => setAssetModalOpen(true)} />
            )}
          </div>
          <Input
            label="Tipo CHPD *"
            value={formData.product_line_label}
            readOnly
            size="md"
            modifier="weak"
          />
          <DropDown
            id="check_format"
            label="Formato de cheque *"
            title={getSelectedCheckFormat()}
            items={checkFormatItems}
            onChange={handleCheckFormatChange}
            size="md"
            marginTop="4px"
          />
        </div>

        {hasAsset && (
          <div style={{ marginBottom: '24px' }}>
            <AssetLoadedBanner
              assetName={assetName}
              quantity={formValues.asset_quantity ?? 0}
              quotation={formValues.asset_quotation ?? 0}
              calculatedAmount={formValues.monetization_amount ?? 0}
              currencyCode={currencyCode}
              onEdit={() => setAssetModalOpen(true)}
              onClear={handleAssetClear}
            />
          </div>
        )}

        {/* Fila 2: Fecha emisión | Fecha vencimiento | Plazo en días | Acreedor */}
        <div data-testid="check-data-row-2" style={{ ...checkDataGridStyle, marginBottom: '24px' }}>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de emisión *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.issue_date}
                onChange={(date: string) => updateFormValues({ issue_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='weak'>
              Fecha de vencimiento *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.due_date}
                onChange={() => {}}
                disabled={true}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
          <Input
            label="Plazo en días *"
            type="number"
            value={formValues.term_in_days?.toString() || ''}
            onChange={(e) => {
              const value = e.target.value;
              const numValue = value === '' ? 0 : parseInt(value, 10);
              if (!isNaN(numValue)) {
                updateFormValues({ term_in_days: numValue });
              }
            }}
            size="md"
            inputStatus="normal"
          />
          <DropDown
            id="creditor"
            label="Acreedor *"
            title={getSelectedCreditor()}
            items={creditorItems}
            onChange={handleCreditorChange}
            size="md"
            marginTop="4px"
          />
        </div>

        {/* Fila 3: Entidad bancaria | Sucursal | Nro de Cheque | Nro de Echeq */}
        <div data-testid="check-data-row-3" style={{ ...checkDataGridStyle, marginBottom: '24px' }}>
          <DropDown
            id="banking_entity"
            label="Entidad bancaria *"
            title={getSelectedBankingEntity()}
            items={creditorItems}
            onChange={handleBankingEntityChange}
            size="md"
            marginTop="4px"
          />
          <Input
            label="Sucursal *"
            value={formValues.bank_branch || ''}
            onChange={(e) => updateFormValues({ bank_branch: e.target.value })}
            size="md"
            maxLength={250}
            inputStatus="normal"
          />
          <Input
            label="Nro de Cheque *"
            value={formValues.check_number || ''}
            onChange={(e) => updateFormValues({ check_number: e.target.value })}
            size="md"
            maxLength={250}
            inputStatus="normal"
          />
          <Input
            label="Nro de Echeq *"
            value={formValues.echeck_number || ''}
            onChange={(e) => updateFormValues({ echeck_number: e.target.value })}
            size="md"
            maxLength={250}
            inputStatus="normal"
          />
        </div>

        {/* Fila 4: Código postal | CMC7 | Plazo de clearing | Fecha estimada de pago */}
        <div data-testid="check-data-row-4" style={{ ...checkDataGridStyle, marginBottom: '24px' }}>
          <Input
            label="Código postal *"
            value={formValues.postal_code || ''}
            onChange={(e) => updateFormValues({ postal_code: e.target.value })}
            size="md"
            maxLength={250}
            inputStatus="normal"
          />
          <Input
            label="CMC7 *"
            value={formValues.cmc7 || ''}
            onChange={(e) => updateFormValues({ cmc7: e.target.value })}
            size="md"
            maxLength={250}
            inputStatus="normal"
          />
          <DropDown
            id="execution-clearing-dropdown"
            label="Plazo de clearing *"
            title={getSelectedClearing()}
            items={clearingItems}
            onChange={handleClearingChange}
            size="md"
            marginTop="4px"
          />
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='weak'>
              Fecha estimada de pago *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.estimated_payment_date || new Date().toISOString().split('T')[0]}
                onChange={() => {}}
                disabled={true}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>
      </Card>

      {/* Datos de librador */}
      <Card margin={{ bottom: '4' }}>
        <div style={{ backgroundColor: '#E6F5F0', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
            Datos de librador
          </H>
        </div>
        
        <div data-testid="check-drawer-row-1" style={{ ...checkDrawerGridStyle, marginBottom: '24px' }}>
          <Input
            label="Librador *"
            value={formValues.drawer?.company_name || ''}
            onChange={(e) => handleDrawerCompanyNameChange(e.target.value)}
            size="md"
            maxLength={250}
            inputStatus="normal"
            placeholder="Librador"
          />
          <Input
            label={`${fiscalId.drawerLabel} *`}
            type="number"
            value={formValues.drawer?.cuit || ''}
            onChange={(e) => handleDrawerCuitChange(e.target.value)}
            size="md"
            maxLength={250}
            onlyNumbers
            inputStatus={cuitLengthError ? 'error' : 'normal'}
            placeholder="0"
          />
        </div>

        {(drawerError || cuitLengthError) && (
          <div style={{ marginTop: '-16px', marginBottom: '24px' }}>
            {cuitLengthError ? (
              <P variant="danger" type="3">
                {cuitLengthError}
              </P>
            ) : null}
            {drawerError ? (
              <P variant="danger" type="3">
                {drawerError}
              </P>
            ) : null}
          </div>
        )}

        <div data-testid="check-drawer-row-2">
          <TextArea
            label="Observación"
            value={formValues.observation || ''}
            onChange={handleObservationChange}
            placeHolder="Ingrese observaciones adicionales (opcional)"
            maxLength={2500}
            rows={4}
            maxLengthHelpText={true}
          />
        </div>
      </Card>

      <AssetSelectionModal
        open={assetModalOpen}
        onClose={() => setAssetModalOpen(false)}
        onAccept={handleAssetAccept}
        currencyCode={currencyCode}
        usdQuotation={usdQuotation}
        initialAssetId={formValues.asset_id}
        initialQuantity={formValues.asset_quantity}
        initialQuotation={formValues.asset_quotation}
      />
    </>
  );
};

export default CheckSection;